if (!customElements.get('product-detail-popup')) {
  customElements.define('product-detail-popup', class ProductDetailPopup extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.querySelector('dialog');
      this.openers = Array.from(this.querySelectorAll('[data-product-popup-open]'));
      this.tabs = Array.from(this.querySelectorAll('.product-detail-popup__tab'));
      this.panels = Array.from(this.querySelectorAll('.product-detail-popup__panel'));
      this.querySelectorAll('[data-product-detail-video]').forEach(wrapper => {
        const video = wrapper.querySelector('video');
        const button = wrapper.querySelector('.video-toggle');
        if (!video || !button) return;
        const updateButton = () => {
          const playing = !video.paused && !video.ended;
          button.dataset.playing = String(playing);
          button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        };
        button.hidden = false;
        button.addEventListener('click', () => {
          if (video.paused) video.play().catch(updateButton);
          else video.pause();
        }, { signal });
        for (const event of ['play', 'pause', 'ended']) {
          video.addEventListener(event, updateButton, { signal });
        }
        updateButton();
      });
      this.reset();
      if (this.hasAttribute('data-product-reviews')) this.setupReviews(signal);

      this.openers.forEach((button, index) => {
        button.addEventListener('click', () => this.open(button, index), { signal });
      });
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.selectTab(index), { signal });
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          else if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.tabs.length - 1;
          else return;
          event.preventDefault();
          this.selectTab(next);
          this.tabs[next].focus();
        }, { signal });
      });
      this.querySelector('[data-product-popup-close]').addEventListener('click', () => this.close(), { signal });
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, { signal });
      this.dialog.addEventListener('animationend', event => {
        if (event.target === this.dialog && event.animationName === 'product-detail-drawer-out') this.dialog.close();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        const opener = this.opener;
        this.opener = null;
        this.reset();
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      }, { signal });
      this.dialog.addEventListener('pointerdown', event => {
        this.backdropPointerDown = event.target === this.dialog;
      }, { signal });
      this.dialog.addEventListener('click', event => {
        if (this.backdropPointerDown && event.target === this.dialog) this.close();
        this.backdropPointerDown = false;
      }, { signal });
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.dialog.querySelectorAll('summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])'))
          .filter(element => element.tabIndex >= 0 && element.getClientRects().length > 0 && !element.closest('[hidden]'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, { signal });
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.reset();
      this.opener = null;
      this.controller?.abort();
      this.reviewsObserver?.disconnect();
    }

    setupReviews(signal) {
      const section = this.closest('.shopify-section');
      if (!section) return;
      this.dialog.addEventListener('click', event => {
        if (!event.target.closest('.yotpo-new-review-btn')) return;
        // Yotpo mounts its form outside this native dialog. Release the modal
        // before its handler opens the form so the form is visible and usable.
        this.opener = null;
        this.dialog.close();
      }, { capture: true, signal });
      const selector = '.product__block--reviews .yotpo-sr-bottom-line-button, [data-product-reviews-open]';
      const updateOpeners = () => {
        this.reviewOpeners = Array.from(section.querySelectorAll(selector));
        this.reviewOpeners.forEach(button => {
          button.setAttribute('aria-haspopup', 'dialog');
          button.setAttribute('aria-controls', this.dialog.id);
          button.setAttribute('aria-expanded', String(this.dialog.open));
        });
      };
      updateOpeners();
      // Yotpo renders its star button asynchronously, and may replace it later.
      this.reviewsObserver = new MutationObserver(updateOpeners);
      section.querySelectorAll('.product__block--reviews').forEach(block => {
        this.reviewsObserver.observe(block, { childList: true, subtree: true });
      });
      const openReviews = event => {
        const button = event.target.closest(selector);
        if (!button || (event.type === 'keydown' && !['Enter', ' '].includes(event.key))) return;
        // Replace Yotpo's scroll-to-widget action before its own handler runs.
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(button);
      };
      section.addEventListener('click', openReviews, { capture: true, signal });
      section.addEventListener('keydown', openReviews, { capture: true, signal });
    }

    open(button, index = -1) {
      if (this.dialog.open) return;
      this.opener = button;
      this.dialog.showModal();
      this.selectTab(index);
      button.setAttribute('aria-expanded', 'true');
      (this.tabs[index] || this.querySelector('[data-product-popup-close]')).focus({ preventScroll: true });
      this.reviewOpeners?.forEach(opener => opener.setAttribute('aria-expanded', 'true'));
      window.pauseAllMedia?.();
    }

    close() {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
      this.dialog.querySelectorAll('video').forEach(video => video.pause());
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.dialog.close();
        return;
      }
      this.dialog.classList.add('is-closing');
      // Complete dismissal even if the animation is interrupted by a style change.
      this.closeTimer = window.setTimeout(() => this.dialog.close(), 300);
    }

    reset() {
      window.clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-closing');
      this.backdropPointerDown = false;
      this.selectTab(-1);
      this.openers.forEach(opener => opener.setAttribute('aria-expanded', 'false'));
      this.reviewOpeners?.forEach(opener => opener.setAttribute('aria-expanded', 'false'));
      this.dialog.scrollTop = 0;
      this.querySelector('.product-detail-popup__surface').scrollTop = 0;
    }

    selectTab(index) {
      this.tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        this.panels[tabIndex].hidden = !selected;
        if (!selected) this.panels[tabIndex].querySelectorAll('video').forEach(video => video.pause());
        this.openers[tabIndex].setAttribute('aria-expanded', String(selected && this.dialog.open));
      });
    }
  });
}

if (!customElements.get('product-formula')) {
  customElements.define('product-formula', class ProductFormula extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.querySelectorAll('.product__formula-dot').forEach(dot => dot.remove());
      const image = this.querySelector('.product__formula-image');
      const ingredients = Array.from(this.querySelectorAll('[data-formula-ingredient]'));
      const descriptions = Array.from(this.querySelectorAll('[data-formula-description]'));
      const dots = new Map();
      const desktopHover = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
      const select = ingredient => {
        ingredients.forEach(button => button.setAttribute('aria-pressed', String(button === ingredient)));
        descriptions.forEach(description => {
          const selected = description.dataset.formulaDescription === ingredient?.dataset.formulaIngredient;
          description.classList.toggle('is-active', selected);
          description.setAttribute('aria-hidden', String(!selected));
        });
        dots.forEach((dot, button) => {
          const selected = button === ingredient;
          dot.hidden = !selected && this.dataset.showIdleDots !== 'true';
          dot.setAttribute('aria-pressed', String(selected));
          if (dot.hidden && document.activeElement === dot) ingredient?.focus({ preventScroll: true });
        });
      };
      const bindSelection = (element, ingredient) => {
        element.addEventListener('pointerenter', event => {
          if (desktopHover.matches && event.pointerType !== 'touch') select(ingredient);
        }, { signal });
        element.addEventListener('focus', () => select(ingredient), { signal });
        element.addEventListener('click', () => select(ingredient), { signal });
      };
      // Keep the original list indices even when an ingredient or position is missing.
      const positionPattern = /^(100|[1-9]?[0-9])x(100|[1-9]?[0-9])$/;
      ingredients.forEach((ingredient, index) => {
        bindSelection(ingredient, ingredient);
        ingredient.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowDown') next = (index + 1) % ingredients.length;
          else if (event.key === 'ArrowUp') next = (index - 1 + ingredients.length) % ingredients.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = ingredients.length - 1;
          else return;
          event.preventDefault();
          ingredients[next].focus({ preventScroll: true });
        }, { signal });
        const position = positionPattern.exec(ingredient.dataset.formulaPosition || '');
        if (!image?.querySelector('img') || !position) return;
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'product__formula-dot';
        dot.style.setProperty('--dot-x', `${position[1]}%`);
        dot.style.setProperty('--dot-y', `${position[2]}%`);
        dot.setAttribute('aria-label', ingredient.textContent.trim().replace(/\s+/g, ' '));
        dot.setAttribute('aria-controls', ingredient.getAttribute('aria-controls'));
        dot.setAttribute('aria-describedby', ingredient.getAttribute('aria-controls'));
        const number = document.createElement('span');
        number.textContent = ingredient.dataset.formulaIngredient;
        number.setAttribute('aria-hidden', 'true');
        dot.append(number);
        image.append(dot);
        dots.set(ingredient, dot);
        bindSelection(dot, ingredient);
      });
      select(ingredients.find(ingredient => ingredient.getAttribute('aria-pressed') === 'true') || ingredients[0]);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  });
}
